import { FunctionComponent } from "react";
import { Controller, UseFormReturn } from "react-hook-form";

import { InputField } from "@/components/InputField/InputField";
import { SelectField } from "@/components/SelectField/SelectField";
import { Toggle } from "@/components/Toggle/Toggle";

import { AiPromptEditor } from "./AiPromptEditor";
import { AiConfigFormValues, AiConfigLabels, AiConfigTargetField } from "./aiConfig.types";
import { getAiVariableOptions, isDropdownField } from "./aiConfig.utils";

interface AiConfigFormPanelProps {
	form: UseFormReturn<AiConfigFormValues>;
	labels: AiConfigLabels;
	targetField?: AiConfigTargetField;
	textFields: AiConfigTargetField[];
}

interface BehaviorCardProps {
	title: string;
	help: string;
	enabled?: boolean;
	toggle?: () => void;
	children: React.ReactNode;
}

const BehaviorCard: FunctionComponent<BehaviorCardProps> = ({
	title,
	help,
	enabled,
	toggle,
	children,
}) => {
	return (
		<div className="rounded-2xl border border-border-light bg-brand-white p-4">
			<div className="flex items-start justify-between gap-4">
				<div>
					<div className="text-sm font-semibold text-text">{title}</div>
					<div className="mt-1 text-xs leading-5 text-text-meta">{help}</div>
				</div>
				{toggle && <Toggle isChecked={Boolean(enabled)} onChange={toggle} />}
			</div>
			<div className="mt-4">{children}</div>
		</div>
	);
};

const renderAllowedValueOption = (
	option: { label: string; value: string },
	selectedFallback?: string
) => {
	const isFallback = selectedFallback === option.value;

	return (
		<span
			key={option.value}
			className={[
				"rounded-full border px-3 py-1 text-xs font-semibold",
				isFallback
					? "border-brand-light bg-background text-brand-light"
					: "border-border-light bg-brand-white text-text",
			].join(" ")}
		>
			{option.label}
			{isFallback ? " (fallback)" : ""}
		</span>
	);
};

export const AiConfigFormPanel: FunctionComponent<AiConfigFormPanelProps> = ({
	form,
	labels,
	targetField,
	textFields,
}) => {
	const {
		control,
		formState: { errors },
		setValue,
		watch,
	} = form;
	const values = watch();
	const allowedValues = targetField?.options?.map((option) => option.value) ?? [];
	const secondaryOptions = textFields.map((field) => ({ title: field.label, value: field.name }));

	const updateValue = <T extends keyof AiConfigFormValues>(
		field: T,
		value: AiConfigFormValues[T]
	) => setValue(field, value as never, { shouldDirty: true, shouldValidate: true });

	return (
		<div
			className="rounded-3xl border border-border-light bg-background p-5"
			data-testid="ai-config-form-panel"
		>
			<div className="mb-5">
				<h3 className="text-sm font-semibold text-text">{labels.configurationTitle}</h3>
			</div>

			<div className="space-y-5">
				<section>
					<div className="mb-2 text-sm font-semibold text-text">{labels.promptLabel}</div>
					<div className="mb-3 text-xs leading-5 text-text-meta">{labels.promptHelp}</div>
					<Controller
						control={control}
						name="prompt"
						render={({ field }) => (
							<AiPromptEditor
								error={
									typeof errors.prompt?.message === "string" ? errors.prompt.message : undefined
								}
								insertVariableLabel={labels.insertVariable}
								metaLabel={labels.promptMeta}
								placeholder={labels.promptPlaceholder}
								value={field.value ?? ""}
								variableOptions={getAiVariableOptions(targetField)}
								variablesLabel={labels.variablesLabel}
								onChange={field.onChange}
							/>
						)}
					/>
				</section>

				{isDropdownField(targetField) && (
					<section className="rounded-2xl border border-border-light bg-brand-white p-4">
						<div className="text-sm font-semibold text-text">{labels.allowedValuesTitle}</div>
						<div className="mt-1 text-xs leading-5 text-text-meta">{labels.fallbackHelp}</div>
						<div className="mt-3 flex flex-wrap gap-2">
							{targetField?.options?.length ? (
								targetField.options.map((option) =>
									renderAllowedValueOption(option, values.fallbackValue)
								)
							) : (
								<span className="text-xs text-text-meta">{labels.allowedValuesEmpty}</span>
							)}
						</div>
					</section>
				)}

				<section>
					<div className="mb-3 text-sm font-semibold text-text">{labels.behaviorTitle}</div>
					<div className="space-y-3">
						<BehaviorCard help={labels.fallbackHelp} title={labels.fallbackLabel}>
							{isDropdownField(targetField) ? (
								<SelectField
									label={labels.fallbackLabel}
									options={allowedValues.map((value) => ({ title: value, value }))}
									placeholder={labels.leaveEmpty}
									selectedOption={values.fallbackValue || null}
									handleSelect={(value) => updateValue("fallbackValue", value?.toString() ?? "")}
								/>
							) : (
								<InputField
									error={errors.fallbackValue?.message}
									label={labels.fallbackLabel}
									name="fallbackValue"
									placeholder={labels.leaveEmpty}
									showError
									value={values.fallbackValue ?? ""}
									handleChange={(event) => updateValue("fallbackValue", event.target.value)}
								/>
							)}
						</BehaviorCard>

						<BehaviorCard
							enabled={values.syncReasoning}
							help={labels.syncReasoningHelp}
							title={labels.syncReasoningLabel}
							toggle={() => updateValue("syncReasoning", !values.syncReasoning)}
						>
							{values.syncReasoning && (
								<div className="border-t border-dashed border-border-light pt-4">
									<SelectField
										error={errors.syncReasoningTargetField?.message}
										label={labels.syncReasoningTargetLabel}
										options={secondaryOptions}
										placeholder={labels.secondaryTargetPlaceholder}
										selectedOption={values.syncReasoningTargetField || null}
										showError
										handleSelect={(value) =>
											updateValue("syncReasoningTargetField", value?.toString() ?? "")
										}
									/>
									<div className="mt-2 text-xs text-text-meta">
										{labels.syncReasoningTargetHelp}
									</div>
								</div>
							)}
						</BehaviorCard>

						<BehaviorCard
							enabled={values.syncSource}
							help={labels.syncSourceHelp}
							title={labels.syncSourceLabel}
							toggle={() => updateValue("syncSource", !values.syncSource)}
						>
							{values.syncSource && (
								<div className="border-t border-dashed border-border-light pt-4">
									<SelectField
										error={errors.syncSourceTargetField?.message}
										label={labels.syncSourceTargetLabel}
										options={secondaryOptions}
										placeholder={labels.secondaryTargetPlaceholder}
										selectedOption={values.syncSourceTargetField || null}
										showError
										handleSelect={(value) =>
											updateValue("syncSourceTargetField", value?.toString() ?? "")
										}
									/>
									<div className="mt-2 text-xs text-text-meta">{labels.syncSourceTargetHelp}</div>
								</div>
							)}
						</BehaviorCard>
					</div>
				</section>
			</div>
		</div>
	);
};
